// 🌐 프로젝트 전용 토픽(slug 'pjt-…') 이름 번역 (2026-09-28 사용자 "좌측 리스트에서는 번역 되지 않았습니다").
//   RC 방 이름(fname '[P] 원문')은 한 개뿐이라 언어별로 둘 수 없다 → 서버 GET /api/tpt/projects/room-names 가 내 프로젝트
//   방(rid)별 프로젝트명 번역(tx)을 주고, 사이드바·방 헤더가 표시만 바꾼다. 번역이 없거나 로드 전이면 원문(종전 그대로).
//   세션당 1회 + 10분 캐시 — 사이드바가 다시 그려질 때마다 부르지 않는다.
import { useEffect, useState } from 'react';
import { listPjRoomNames } from '../../api/projects';
import { pjText, swapPjNameInTitle, type PjTxMap } from './pjTx';

const TTL = 10 * 60_000;
let names = new Map<string, PjTxMap>();
let byName: Array<[string, PjTxMap]> = [];   // 원문 프로젝트명 → 번역 (옛 알림 카드 제목용, 2026-09-29)
let loadedAt = 0;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function load(): Promise<void> {
	if (inflight) return inflight;
	if (loadedAt && Date.now() - loadedAt < TTL) return Promise.resolve();
	inflight = listPjRoomNames()
		.then((rows) => {
			names = new Map(rows.filter((r) => r.rid).map((r) => [r.rid, r.tx]));
			byName = rows.filter((r) => r.name && r.name.trim().length >= 2).map((r) => [String(r.name).trim(), r.tx]);
			loadedAt = Date.now();
			listeners.forEach((fn) => fn());
		})
		.catch(() => { loadedAt = Date.now(); })   // 실패(구 사이드카 404 등)도 10분은 다시 묻지 않는다 — 원문 표시로 충분
		.finally(() => { inflight = null; });
	return inflight;
}

/** 프로젝트 토픽의 내 언어 이름 — 번역이 없으면 '' (호출부가 원문 폴백) */
export function pjRoomName(rid: string | undefined): string {
	if (!rid) return '';
	const tx = names.get(rid);
	return tx ? pjText({ name: '', tx } as Parameters<typeof pjText>[0], 'name') : '';
}

/** 알림 카드 제목 속 원문 프로젝트명 → 내 언어 번역본 (없으면 그대로) */
export function pjNoticeTitle(title: string): string {
	if (!title || !byName.length) return title;
	return swapPjNameInTitle(title, byName.map(([o, tx]) => [o, pjText({ name: '', tx } as Parameters<typeof pjText>[0], 'name')] as const));
}

/** 로드 트리거 + 로드되면 다시 그리기 */
export function usePjRoomNames(): number {
	const [ver, setVer] = useState(0);
	useEffect(() => {
		const fn = () => setVer((v) => v + 1);
		listeners.add(fn);
		void load();
		return () => { listeners.delete(fn); };
	}, []);
	return ver;
}

/** 🏷 방 표시 이름 (2026-09-29 사용자 "PJT 명칭: [P] Tìm hiểu… 로 보임") — 프로젝트 전용 토픽(slug pjt-)이면 `[P] 번역본`, 그 외 fname·name.
 *  화면 표시 전용. 서버로 보내는 값(예약 전송·업무 카드 방 이름)이나 토픽 이름 편집칸에는 쓰지 않는다(원문 유지). */
export function roomTitleOf(r: { _id?: string; t?: string; name?: string; fname?: string } | null | undefined): string {
	if (!r) return '';
	if (r.t === 'p' && /^pjt-/.test(r.name || '')) {
		const tx = pjRoomName(r._id);
		if (tx) return `[P] ${tx}`;
	}
	return r.fname || r.name || '';
}
